/** Terms & Conditions section content — UK TO MARS LTD */

export const termsIntro = [
  "These Terms and Conditions apply to all website use, enquiries, quotations, sales, wholesale supply, export services and related services provided by UK TO MARS LTD, company number 15764998, registered at 15 Little Balmer, Buckingham Industrial Estate, Buckingham, MK18 1TF, United Kingdom.",
  "By using our website, submitting an enquiry, requesting a quotation or placing an order, you agree to these Terms and Conditions.",
];

export const termsSections = [
  {
    title: "Definitions",
    paragraphs: ["In these Terms:"],
    bullets: [
      '"Company", "we", "us", "our" means UK TO MARS LTD.',
      '"Customer", "Buyer", "you" means the business, person or organisation requesting a quote or purchasing goods or services from us.',
      '"Goods" means products supplied or sourced by us.',
      '"Services" means wholesale supply, sourcing, consolidation, loading, logistics coordination, export documentation support and related services.',
      '"Order" means any confirmed request to purchase goods or services.',
      '"Quotation" means a price or availability offer issued by us before order acceptance.',
      '"FCA" means Free Carrier under Incoterms® 2020, with our premises as the named place of delivery unless another place is agreed in writing.',
      '"Incoterms" means the Incoterms® 2020 rules published by the International Chamber of Commerce (ICC), or such later version as may be in force at the time of contract.',
      '"Business Day" means a day other than Saturday, Sunday or a public holiday in England.',
    ],
  },
  {
    title: "Business Customers Only",
    paragraphs: [
      "Our website and services are intended for business customers only, including wholesalers, distributors, retailers, supermarkets, convenience stores, online sellers, importers and trade buyers.",
      "By placing an enquiry or order, you confirm that you are acting for business purposes and not as a consumer.",
    ],
  },
  {
    title: "Website Use",
    paragraphs: ["You agree to use our website lawfully and responsibly.", "You must not:"],
    bullets: [
      "misuse the website;",
      "copy, scrape, download or reproduce website content without permission;",
      "use automated tools, bots or data extraction systems;",
      "interfere with website security or functionality;",
      "provide false or misleading information;",
      "use our website or services for unlawful purposes.",
    ],
    closing: [
      "We may suspend or restrict access to any account if we reasonably believe these Terms have been breached.",
    ],
  },
  {
    title: "Accounts and Login Details",
    paragraphs: [
      "Customers may create an account or have an account created during the enquiry process.",
      "You are responsible for:",
    ],
    bullets: [
      "keeping login details confidential;",
      "ensuring account information is accurate;",
      "updating company, delivery, VAT, EORI or contact details where required;",
      "all activity carried out under your account.",
    ],
    closing: ["We may refuse, suspend or close an account at our discretion."],
  },
  {
    title: "Product Information, Images and Availability",
    paragraphs: [
      "Product descriptions, images, pack sizes, barcodes, ingredients, allergens, nutritional content, country of origin, HS codes, case sizes and other product information published on our website or shared in quotations are provided in good faith and to the best of our knowledge at the time of publication.",
      "Manufacturers may reformulate products, update ingredients, change allergen declarations, alter nutritional content, modify packaging, change pack sizes or update product specifications at any time without prior notice. We make no warranty that the information displayed will match the product supplied at the time of dispatch.",
      "Product images are for illustrative purposes only and may not reflect current packaging, branding, language variants, pack sizes or country-specific labelling.",
      "Customers are responsible for:",
    ],
    bullets: [
      "inspecting all products on receipt;",
      "verifying ingredients, allergens, nutritional content and product specifications against the actual product;",
      "ensuring that products meet the legal, labelling, language and regulatory requirements of the destination market;",
      "confirming product compliance with food, safety, health, cosmetic, pharmaceutical or other applicable laws of the country of resale or end use;",
      "confirming product specifications prior to onward sale or distribution.",
    ],
    closing: [
      "Stock availability is not guaranteed until confirmed by us in writing. Product substitutions may be offered where appropriate.",
      "We accept no liability for any loss, damage, or non-compliance arising from reliance on website or quotation information without independent verification by the customer.",
    ],
  },
  {
    title: "Quotations",
    paragraphs: ["All prices and quotations are subject to:"],
    bullets: [
      "availability;",
      "supplier confirmation;",
      "currency movements;",
      "transport costs;",
      "tax, duty or regulatory changes;",
      "final written confirmation by us.",
    ],
    closing: [
      "A quotation does not create a binding contract unless accepted and confirmed by us in writing.",
      "Unless otherwise stated, quotations are valid for 2 days from the date issued.",
      "We reserve the right to amend or withdraw quotations before order acceptance.",
    ],
  },
  {
    title: "Orders and Acceptance",
    paragraphs: [
      "All orders are subject to acceptance by UK TO MARS LTD.",
      "An order is accepted only when we issue written confirmation, invoice, pro forma invoice acceptance, or dispatch the goods.",
      "We may refuse, cancel, reduce or amend an order where:",
    ],
    bullets: [
      "stock is unavailable;",
      "supplier availability changes;",
      "payment is not received;",
      "compliance checks are not satisfied;",
      "export or destination requirements cannot be met;",
      "the order is commercially or legally unsuitable.",
    ],
    closing: [
      "For sourced, special, short-dated, clearance, promotional or customer-specific goods, cancellation may not be accepted once we have committed to purchase or allocate stock.",
    ],
  },
  {
    title: "Contract Formation and Prevailing Terms",
    paragraphs: [
      "A quotation is an invitation to treat and is not an offer capable of acceptance. An order placed by the Customer is an offer to purchase the goods or services on these Terms. No contract is formed until we accept that order in writing — by order confirmation, accepted pro forma invoice, or dispatch of the goods — at which point a binding contract comes into existence on these Terms.",
      "Each contract incorporates these Terms together with the relevant quotation and order confirmation. By submitting an enquiry, accepting a quotation, registering an account or placing an order, the Customer agrees to these Terms.",
      "These Terms apply to the contract to the exclusion of any other terms that the Customer seeks to impose or incorporate, or which are implied by trade, custom, practice or course of dealing, including any terms contained or referred to in the Customer's purchase order, order acknowledgement, specification or other document. No acknowledgement, confirmation, performance of an order or other conduct by us shall be treated as acceptance of the Customer's terms.",
    ],
  },
  {
    title: "Minimum Order Value",
    paragraphs: [
      "Minimum wholesale order value requirements apply.",
      "Unless otherwise agreed in writing, the minimum order value is:",
      "£3,000 FCA (Incoterms® 2020) excluding VAT.",
    ],
  },
  {
    title: "Pricing and VAT",
    paragraphs: ["Unless otherwise stated:"],
    bullets: [
      "prices are in GBP (£);",
      "prices exclude VAT;",
      "prices exclude customs duties, import taxes, destination charges and local taxes;",
      "prices exclude delivery unless expressly agreed.",
    ],
    closing: [
      "VAT treatment depends on the nature of the supply, destination and export evidence.",
      "For qualifying export orders, goods may be zero-rated for VAT only where HMRC requirements are met, including valid evidence that the goods have left the UK.",
      "If goods are collected by the customer or their forwarder, VAT may be charged initially. Once valid export evidence is received within the required HMRC timeframe, we may process a VAT refund or account credit, subject to verification.",
      "If export evidence is not provided or does not meet HMRC requirements, VAT will remain payable.",
    ],
  },
  {
    title: "Payment Terms",
    paragraphs: [
      "Payment terms will be stated on the invoice or agreed in writing.",
      "Unless otherwise agreed:",
    ],
    bullets: [
      "payment must be made in full before dispatch or release of goods;",
      "funds must be cleared in our bank account before collection or shipment;",
      "bank charges, exchange fees and transfer costs are the customer's responsibility.",
    ],
    closing: [
      "We may withhold goods, documents, delivery, collection or further supply until payment is received in full.",
      "Late payment may result in:",
    ],
    bulletsAfter: [
      "suspension of orders;",
      "withdrawal of credit terms;",
      "recovery costs;",
      "interest and charges where permitted by law.",
    ],
    closing2: [
      "Customers may not withhold or offset payment because of any claim, dispute or alleged issue unless agreed in writing by us.",
    ],
  },
  {
    title: "Delivery, Collection and Incoterms",
    paragraphs: [
      "Unless otherwise agreed in writing, export sales are supplied on an FCA (Free Carrier) basis under Incoterms® 2020, named at our UK premises.",
      "Under FCA, we complete UK export clearance and load the goods onto the vehicle that you (or your nominated freight forwarder) send to collect. From the point the goods are loaded onto the collecting vehicle, risk passes to you (see clause 13) and you are responsible for:",
    ],
    bullets: [
      "collection from our warehouse;",
      "arranging and paying for the main carriage and freight from our premises;",
      "transport;",
      "insurance;",
      "import clearance and destination-country compliance;",
      "destination customs clearance;",
      "duties, taxes and local charges.",
    ],
    closing: [
      "Where we arrange delivery or logistics support, this will be agreed separately and may involve third-party carriers or freight forwarders.",
      "Where you require us to arrange carriage or delivery, we can supply under other Incoterms® 2020 rules by written agreement — for example CPT or CIP (we arrange and pay the main carriage; CIP also includes insurance), DAP or DPU (we deliver to a named place in the destination country, with DPU including unloading), or DDP (we additionally clear the goods for import and pay import duties and taxes). The applicable rule, named place and any agreed services will be confirmed on the quotation and order confirmation.",
      "Delivery dates are estimates only and are not guaranteed. We are not liable for delays caused by suppliers, carriers, customs, ports, border checks, strikes, weather, force majeure or other circumstances outside our control.",
    ],
  },
  {
    title: "Risk and Title",
    paragraphs: [
      "Risk in the goods passes to the customer:",
    ],
    bullets: [
      "for FCA orders, when the goods are loaded onto the collecting vehicle at our premises; or",
      "at the point specified under the agreed delivery term.",
    ],
    closing: [
      "Title to goods remains with UK TO MARS LTD until we receive full cleared payment for all amounts due. The passing of risk and the passing of title are separate: risk passes on delivery under the agreed Incoterms® 2020 rule, while title (ownership) passes only on receipt of full cleared payment.",
      "Until title passes, the customer must:",
    ],
    bulletsAfter: [
      "store goods safely;",
      "keep goods identifiable where possible;",
      "not remove identifying marks;",
      "not pledge or charge the goods.",
    ],
    closing2: [
      "If payment is overdue, we may recover goods to which title has not passed, where legally permitted.",
    ],
  },
  {
    title: "Inspection, Shortages and Damage",
    paragraphs: [
      "Customers must inspect goods immediately upon collection or delivery.",
      "Any shortages, damages or discrepancies must be reported in writing within 48 hours of collection or delivery, supported by clear evidence such as photographs, delivery notes and product details.",
      "Claims made after this period may not be accepted.",
      "For goods collected by the customer or their forwarder, customers should check goods before departure from the warehouse. Once goods leave the warehouse, shortages or damages may be difficult to verify.",
    ],
  },
  {
    title: "Returns and Cancellations",
    paragraphs: [
      "Returns are accepted only with our prior written approval.",
      "Returned goods must be:",
    ],
    bullets: [
      "unopened;",
      "unused;",
      "in original packaging;",
      "in resaleable condition;",
      "supported by proof of purchase.",
    ],
    closing: ["We may refuse returns for:"],
    bulletsAfter: [
      "short-dated stock;",
      "clearance stock;",
      "sourced-to-order goods;",
      "special-order goods;",
      "frozen or temperature-sensitive goods;",
      "damaged goods caused after collection or delivery;",
      "goods no longer suitable for resale.",
    ],
    closing2: [
      "Restocking, handling, storage, return transport or administration charges may apply.",
    ],
  },
  {
    title: "Shelf Life and Storage",
    paragraphs: [
      "Goods are supplied within available manufacturer or supplier shelf life.",
      "If a customer requires a specific minimum shelf life, this must be requested and agreed in writing before order confirmation.",
      "Customers are responsible for ensuring goods are stored, handled and transported according to manufacturer requirements, including temperature, humidity, hygiene and product safety requirements.",
      "We are not responsible for loss, damage, deterioration or reduced shelf life caused by incorrect storage, transport or handling after risk has passed.",
    ],
  },
  {
    title: "Export and International Trade",
    paragraphs: [
      "Customers are responsible for ensuring that goods can be legally imported, sold and used in the destination country.",
      "This includes responsibility for:",
    ],
    bullets: [
      "import licences;",
      "product registration;",
      "destination customs clearance;",
      "local taxes and duties;",
      "labelling and language requirements;",
      "product restrictions;",
      "health, food, cosmetic, pharmaceutical or safety regulations;",
      "sanctions and restricted-party compliance.",
    ],
    closing: [
      "We may request information including final destination, consignee details, end-user details, import permits, EORI/VAT numbers or other compliance information before accepting or dispatching an order.",
      "We may refuse or cancel any order where we believe there is legal, regulatory, sanctions, customs or reputational risk.",
    ],
  },
  {
    title: "Export Documentation",
    paragraphs: [
      "We assist in preparing and coordinating export-related documentation where agreed.",
      "Documentation may include:",
    ],
    bullets: [
      "commercial invoices;",
      "packing lists;",
      "Certificates of Origin;",
      "Bills of Lading;",
      "health certificates;",
      "Certificates of Free Sale;",
      "EUR1 documents;",
      "ENS (Entry Summary Declarations);",
      "T1 transit/security documents;",
      "other documents required for agreed shipments.",
    ],
    closing: [
      "Some documents may require input, approval or issue by third parties such as freight forwarders, customs agents, chambers of commerce, official veterinarians, carriers or government authorities.",
      "We are not liable for delays caused by incomplete customer information, third-party document delays, customs decisions or destination-country requirements.",
    ],
  },
  {
    title: "VAT Export Evidence",
    paragraphs: [
      "For export orders, zero-rating of VAT is subject to HMRC rules.",
      "Customers must provide any required export evidence, including transport documents, customs evidence, bills of lading, CMRs, airway bills, freight invoices, proof of shipment, proof of destination or other acceptable evidence.",
      "If evidence is not received within HMRC time limits or is not acceptable, we reserve the right to charge VAT.",
      "Where VAT has been charged and valid export evidence is later received and accepted, we may issue a VAT credit or refund in accordance with HMRC requirements.",
    ],
  },
  {
    title: "Labelling, Packaging and Market Compliance",
    paragraphs: [
      "We may support customers with labelling, translation and packaging coordination where agreed.",
      "However, customers remain responsible for ensuring that all labelling, packaging, product claims, language, ingredients, allergens, nutritional information and regulatory requirements comply with the laws of the destination market.",
      "We do not accept liability for resale, import or labelling issues in destination countries unless caused directly by our negligence and confirmed in writing.",
    ],
  },
  {
    title: "Restricted and Regulated Products",
    paragraphs: [
      "Certain goods may be subject to special legal requirements, including medicines, alcohol, tobacco, food supplements, cosmetics, dairy products, meat products, baby products, chemicals or other regulated goods.",
      "Customers must hold all licences, approvals and authorisations required to purchase, import, distribute or resell such goods.",
      "We may refuse supply where documents, licences or compliance evidence are not provided.",
    ],
  },
  {
    title: "Product Recalls and Withdrawals",
    paragraphs: [
      "We are a wholesaler and exporter, not the manufacturer.",
      "If a product recall, withdrawal or safety notice is issued by a manufacturer, brand owner, supplier or authority, we will reasonably assist customers where possible.",
      "Customers must cooperate with recall instructions and provide information required to trace affected goods.",
      "We are not responsible for losses arising from manufacturer recalls, brand withdrawals or regulatory actions unless required by law.",
    ],
  },
  {
    title: "Shipment Tracking",
    paragraphs: [
      "Live shipment tracking may be available for selected shipments.",
      "Tracking availability depends on the destination country, route, carrier, equipment, technology coverage and logistics provider.",
      "Tracking updates may not always be real-time and are not guaranteed for every shipment or destination.",
      "Customers should confirm tracking availability with their account manager before shipment dispatch.",
    ],
  },
  {
    title: "Pallets and Returnable Equipment",
    paragraphs: [
      "Where pallets, crates, cages or returnable equipment are supplied or used, the customer is responsible for their safe handling and return where required.",
      "Charges may apply for lost, damaged, unreturned or misused pallets or equipment.",
    ],
  },
  {
    title: "Confidentiality",
    paragraphs: [
      "All quotations, pricing, supplier information, product availability, commercial terms and business correspondence are confidential.",
      "Customers must not disclose our pricing, quotations, supplier details or commercial information to third parties without our written permission.",
    ],
  },
  {
    title: "Intellectual Property",
    paragraphs: [
      "All website content, designs, text, images, graphics, product data, logos and materials are owned by or licensed to UK TO MARS LTD unless otherwise stated.",
      "Customers may use website content only for the purpose of purchasing or enquiring about our goods and services.",
      "No content may be copied, reproduced, scraped, distributed, modified or used commercially without our prior written consent.",
      "All third-party brand names and trademarks belong to their respective owners.",
    ],
  },
  {
    title: "Limitation of Liability",
    paragraphs: [
      "Nothing in these Terms excludes liability that cannot legally be excluded, including liability for death or personal injury caused by negligence, fraud or fraudulent misrepresentation.",
      "Subject to this, our total liability is limited to the value of the goods or services giving rise to the claim.",
      "We are not liable for:",
    ],
    bullets: [
      "loss of profit;",
      "loss of business;",
      "loss of contracts;",
      "loss of opportunity;",
      "indirect or consequential loss;",
      "customs delay;",
      "destination-country restrictions;",
      "carrier delays;",
      "customer resale losses.",
    ],
  },
  {
    title: "Force Majeure",
    paragraphs: [
      "We are not liable for delay or failure to perform caused by events outside our reasonable control, including but not limited to supplier failure, transport disruption, port congestion, customs delay, border closure, strikes, labour shortages, fuel shortages, fire, flood, war, terrorism, sanctions, government action, pandemics, IT failure, cyber incidents or severe weather.",
    ],
  },
  {
    title: "Compliance, Sanctions and Anti-Bribery",
    paragraphs: [
      "Both parties must comply with all applicable UK and international laws, including sanctions, anti-bribery (including the Bribery Act 2010), anti-corruption, anti-money laundering (including the Proceeds of Crime Act 2002), modern slavery (including the Modern Slavery Act 2015) and export-control laws.",
      "Customers must not use our goods or services for unlawful purposes or supply goods to restricted persons, countries or territories.",
      "We may cancel orders where compliance concerns arise.",
    ],
  },
  {
    title: "Data Protection",
    privacyLink: true,
  },
  {
    title: "Electronic Communication",
    paragraphs: [
      "Orders, confirmations, quotations, invoices, notices and contract communications may be sent electronically by email, website forms, account portals or other agreed communication channels.",
      "Electronic communications and accepted electronic signatures may be treated as written communication where permitted by law.",
    ],
  },
  {
    title: "Changes to These Terms",
    paragraphs: [
      "We may update these Terms and Conditions from time to time.",
      "The latest version will be published on our website and will apply to website use, new enquiries, quotations and orders after publication.",
    ],
  },
  {
    title: "Currency and Exchange",
    paragraphs: [
      "Where prices are quoted in a currency other than GBP, the applicable exchange rate, conversion fees and bank charges may affect the final amount payable.",
      "Currency conversion costs, foreign exchange losses and bank fees are the customer's responsibility unless expressly agreed otherwise in writing.",
    ],
  },
  {
    title: "Customer Indemnity",
    paragraphs: [
      "The customer shall indemnify and hold harmless UK TO MARS LTD against any claims, losses, damages, fines, penalties, costs or expenses (including legal costs) arising from:",
    ],
    bullets: [
      "the customer's breach of these Terms;",
      "the customer's breach of applicable laws or regulations in the destination market;",
      "incorrect, incomplete or misleading information provided by the customer;",
      "non-compliance with import, labelling, product safety, sanctions or export-control requirements;",
      "third-party claims arising from the customer's resale, marketing or use of the goods.",
    ],
  },
  {
    title: "Entire Agreement",
    paragraphs: [
      "These Terms and Conditions, together with any written quotation, order confirmation, invoice or specific written agreement between the parties, constitute the entire agreement between UK TO MARS LTD and the Customer in respect of the subject matter and supersede all prior agreements, representations, understandings and arrangements, whether written or oral.",
      "The Customer acknowledges that, in entering into a contract, it has not relied on any statement, representation or warranty not expressly set out in these Terms.",
    ],
  },
  {
    title: "Severance",
    paragraphs: [
      "If any provision or part-provision of these Terms is found by a court or competent authority to be invalid, illegal or unenforceable, that provision or part-provision shall be deemed modified to the minimum extent necessary to make it valid, legal and enforceable.",
      "If such modification is not possible, the relevant provision or part-provision shall be deemed deleted. The remaining provisions of these Terms shall continue in full force and effect.",
    ],
  },
  {
    title: "Third Party Rights",
    paragraphs: [
      "No person other than the parties to these Terms shall have any rights to enforce any of these Terms under the Contracts (Rights of Third Parties) Act 1999 or otherwise.",
    ],
  },
  {
    title: "No Waiver",
    paragraphs: [
      "A failure or delay by UK TO MARS LTD to exercise any right or remedy under these Terms shall not constitute a waiver of that right or remedy.",
      "A waiver of any right or remedy shall only be effective if given in writing and shall not be deemed a waiver of any subsequent breach or default.",
    ],
  },
  {
    title: "Assignment",
    paragraphs: [
      "The Customer may not assign, transfer, sub-contract or deal in any way with any of its rights or obligations under these Terms without our prior written consent.",
      "UK TO MARS LTD may assign, transfer or sub-contract any of its rights or obligations to a group company or in connection with a sale or restructuring of the business.",
    ],
  },
  {
    title: "Notices",
    paragraphs: [
      "Any notice required to be given under these Terms shall be in writing and shall be sent to the address, email address or other contact details last notified by the relevant party.",
      "Notices shall be deemed received:",
    ],
    bullets: [
      "if delivered by hand, at the time of delivery;",
      "if sent by pre-paid first-class post, 48 hours after posting;",
      "if sent by email, at the time of transmission, provided no delivery failure notification is received.",
    ],
  },
  {
    title: "Governing Law and Jurisdiction",
    paragraphs: [
      "These Terms and Conditions are governed by the laws of England and Wales.",
      "The courts of England and Wales shall have exclusive jurisdiction over disputes arising from these Terms, unless otherwise agreed in writing.",
    ],
  },
  {
    title: "Contact Details",
    paragraphs: [
      "For questions about this Terms and Conditions, please contact:",
    ],
    contact: true,
  },
];
